import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/base/Button';
import FilterTab, { FilterTabItem } from 'components/common/FilterTab';
import PageBreadcrumb from 'components/common/PageBreadcrumb';
import SearchBox from 'components/common/SearchBox';
import UsersTable, { getUsersTableColumns } from 'components/tables/UsersTable';
import { defaultBreadcrumbItems } from 'data/commonData';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { ChangeEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User } from 'services/types';
import userService from 'services/users/userService';

const tabItems: FilterTabItem[] = [
  {
    label: 'All',
    value: 'all',
    count: 0
  },
  {
    label: 'Active',
    value: 'active',
    count: 0
  },
  {
    label: 'Inactive',
    value: 'inactive',
    count: 0
  },
  {
    label: 'Suspended',
    value: 'suspended',
    count: 0
  }
];

const UsersList = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const usersData = await userService.getAll();
        console.log('Fetched users data:', usersData);
        setUsers(Array.isArray(usersData) ? usersData : []);
        setError(null);
      } catch (err) {
        console.error('Error fetching users:', err);
        setError('Failed to load users');
        setUsers([]); // Ensure users is always an array
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const handleCreateUser = () => {
    navigate('/app/users/create');
  };

  const handleEditUser = (user: User) => {
    navigate(`/app/users/edit/${user.id}`);
  };

  const table = useAdvanceTable({
    data: users,
    columns: getUsersTableColumns(handleEditUser),
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: true
  });

  // Update the search handler to use the table
  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    table.setGlobalFilter(e.target.value || undefined);
  };
  if (loading) {
    return (
      <div>
        <PageBreadcrumb items={defaultBreadcrumbItems} />
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: '400px' }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <PageBreadcrumb items={defaultBreadcrumbItems} />
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-9">
        <h2 className="mb-4">Users</h2>
        <FilterTab tabItems={tabItems} className="mb-2" />

        {users && users.length > 0 ? (
          <AdvanceTableProvider {...table}>
            <div className="mb-4">
              <div className="d-flex flex-wrap gap-3">
                <SearchBox placeholder="Search users" onChange={handleSearch} />
                <div className="ms-xxl-auto">
                  <Button variant="primary" onClick={handleCreateUser}>
                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                    Novo usuário
                  </Button>
                </div>
              </div>
            </div>

            <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-white border-top border-bottom border-200 position-relative top-1">
              <UsersTable />
            </div>
          </AdvanceTableProvider>
        ) : (
          <div className="text-center py-5">
            <p className="text-muted">No users found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UsersList;
